
import { User, HelpCircle, Bell, Menu } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCompactLayout } from "@/hooks/use-layout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSidebar } from "@/components/ui/sidebar";
import { useBroadcastNotifications } from "@/hooks/useBroadcastNotifications";
import { useNavigateToSettingsTab } from "@/utils/navigateToSettingsTab";
import { useAppShell } from "./AppShellContext";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const { user, signOut } = useAuth();
  const isCompact = useCompactLayout();
  const { showSidebar } = useAppShell();
  const { toggleSidebar } = useSidebar();
  const location = useLocation();
  const { unreadCount = 0 } = useBroadcastNotifications();
  const goToMessagesTab = useNavigateToSettingsTab("messages");

  const showMenuButton = showSidebar && isCompact;

  return (
    <header className="fixed top-0 left-0 w-full bg-white z-50 shadow-md">
      <div className="flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 py-2 sm:py-3 max-w-screen-2xl mx-auto min-h-[var(--header-h,72px)]">
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          {showMenuButton && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 h-10 w-10"
              onClick={toggleSidebar}
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          )}

          <Link to="/welcome" className="flex items-center gap-2 hover:opacity-90 transition-opacity min-w-0">
            <img
              src="/images/alunatools/e2b3cf9e-19de-4b54-9306-7b53664ced03.png"
              alt="AlunaTools Logo"
              className="h-8 w-auto sm:h-9 shrink-0"
            />
            <span className="hidden sm:inline text-lg sm:text-xl font-semibold font-nunito truncate">
              AlunaTools
            </span>
          </Link>
        </div>

        {!user && !isCompact && (
          <p className="hidden md:block flex-1 text-center text-xs lg:text-sm text-blue-600 px-2">
            <Link to="/auth" className="hover:underline">
              Inscrivez-vous gratuitement pour utiliser tous les outils
            </Link>
          </p>
        )}

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/faq" className="p-2 hover:opacity-80 transition-opacity" aria-label="FAQ">
                  <HelpCircle className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent><p>Aide et FAQ</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="relative p-2 hover:opacity-80 transition-opacity"
                  onClick={goToMessagesTab}
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {user && unreadCount > 0 && (
                    <span
                      className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-white"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent><p>Notifications</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none p-2" aria-label="Compte">
              <User className="h-5 w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {user ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="w-full">Paramètres</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut}>Déconnexion</DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem asChild>
                  <Link to="/auth" state={{ from: location }} className="w-full">
                    Connexion / Inscription
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
